import type { SimpleBinding } from '@doer/frontend-components/creativity-editor';
import type { Binder } from './binder';
import type { RoutedFormData } from '../../../../types/templates';
import { setAtPath } from '../core/binding-map';
import { resolveValuePath } from '../core/value-path';

const isNonEmpty = (v: unknown): boolean => v != null && v !== '';

function resolveValue(
  formData: RoutedFormData,
  defaults: RoutedFormData,
  binding: SimpleBinding,
  resolutionId?: string
): unknown {
  const fromForm = resolveValuePath(formData[binding.fieldKey], binding.valuePath);
  let value: unknown = isNonEmpty(fromForm) ? fromForm : defaults[binding.fieldKey];
  // Defaults can be per-resolution objects: { '1080x1920-A': 'foo', ... }
  if (
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    resolutionId &&
    Object.prototype.hasOwnProperty.call(value, resolutionId)
  ) {
    value = (value as Record<string, unknown>)[resolutionId];
  }
  return value;
}

export const simpleBinder: Binder<SimpleBinding> = {
  resolveTargets(binding, formData, defaults, resolutionId) {
    return [
      {
        pieceId: binding.pieceId,
        value: resolveValue(formData, defaults, binding, resolutionId),
        bindingType: binding.bindingType
      }
    ];
  },

  pieceMapEntries(binding) {
    return [
      [
        binding.pieceId,
        {
          fieldKey: binding.fieldKey,
          kind: 'simple',
          bindingType: binding.bindingType,
          valuePath: binding.valuePath
        }
      ]
    ];
  },

  computeNextFieldValue(entry, formData, newValue) {
    if (entry.kind !== 'simple') return newValue;
    if (!entry.valuePath) return newValue;
    const current = formData[entry.fieldKey];
    const base = current != null && typeof current === 'object' ? (current as Record<string, unknown>) : {};
    return setAtPath(base, entry.valuePath, newValue);
  }
};
